import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiFetch, clearAuthToken } from '../api/client';
import OpsLayout from '../components/Layout/OpsLayout';
import CauseConsumerPreview, { defaultHeroModeForImageMeta, normalizeHeroMode } from '../components/CauseConsumerPreview';
import './OpsNfps.css';

const ACTION_CONFIG = {
  proof: {
    label: 'Update Proof Status',
    endpointKey: 'proof',
    options: ['unsubmitted', 'pending_review', 'verified', 'rejected'],
    reasonCodes: [
      'proof_docs_missing',
      'proof_docs_unreadable',
      'ein_mismatch',
      'name_mismatch',
      'verified',
      'rejected_fraud_risk',
      'other',
    ],
  },
  moderation: {
    label: 'Update Moderation Status',
    endpointKey: 'moderation',
    options: ['not_submitted', 'pending', 'approved', 'needs_action', 'rejected'],
    reasonCodes: [
      'missing_profile_assets',
      'inappropriate_content',
      'copyright_or_trademark',
      'misleading_claims',
      'approved',
      'needs_edits',
      'other',
    ],
  },
  registration: {
    label: 'Update Registration Status',
    endpointKey: 'registration',
    options: ['draft', 'submitted', 'needs_action', 'under_review', 'approved', 'rejected', 'withdrawn'],
    reasonCodes: [
      'missing_required_info',
      'invalid_org_structure_or_tax',
      'ein_issue',
      'areas_served_issue',
      'signatory_issue',
      'duplicate_org',
      'policy_ineligible',
      'withdrawn_by_request',
      'approved_after_fix',
      'other',
    ],
  },
  causeStatus: {
    label: 'Activate/Inactivate Cause',
    endpointKey: 'causeStatus',
    options: ['active', 'inactive'],
    reasonCodesByStatus: {
      active: [
        'approved_after_review',
        'approved_after_fix',
        'reactivated_by_ops',
        'reactivated_by_request',
        'other',
      ],
      inactive: [
        'policy_ineligible',
        'withdrawn_by_request',
        'other',
      ],
    },
  },
  addNote: {
    label: 'Add Audit Note',
    endpointKey: 'addNote',
    method: 'POST',
    options: [],
    reasonCodes: [],
  },
};
const IMAGE_DO_NOT_USE_REASON_CODES = [
  'low_quality',
  'incorrect_branding',
  'copyright_or_rights',
  'graphic_or_sensitive',
  'duplicate',
  'other',
];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

function formatUploadLimitLabel(bytes) {
  const mb = Number(bytes) / (1024 * 1024);
  return Number.isInteger(mb) ? `${mb} MB` : `${mb.toFixed(1)} MB`;
}

function friendlyUploadError(message, { kind = 'generic' } = {}) {
  const raw = String(message || '').trim();
  const label = kind === 'logo'
    ? 'logo'
    : kind === 'image'
      ? 'image'
      : 'file';
  const selected = label === 'logo' ? 'selected logo' : label === 'image' ? 'selected image' : 'selected file';
  const alternate = label === 'file' ? 'different file' : 'different image';
  const match = raw.match(/File exceeds\s+(\d+)\s*MB limit\.?/i);
  if (match) {
    const limitLabel = `${match[1]} MB`;
    if (label === 'logo' || label === 'image') {
      return `Image too large. Please choose a file that is ${limitLabel} or smaller.`;
    }
    return `File too large. Please choose a file that is ${limitLabel} or smaller.`;
  }
  if (/unsupported content type/i.test(raw)) {
    if (label === 'logo') return 'Please upload a logo image file, such as a JPG, PNG, or WebP.';
    if (label === 'image') return 'Please upload an image file, such as a JPG, PNG, or WebP.';
    return 'Please upload a supported file type.';
  }
  if (/invalid file size|missing file|empty/i.test(raw)) {
    return label === 'file'
      ? "We couldn't read that file. Please try a different file."
      : "We couldn't read that image file. Please try a different image.";
  }
  if (/service temporarily unavailable|s3 upload provider is not enabled|s3 upload bucket is not configured/i.test(raw)) {
    return label === 'logo'
      ? 'Logo upload is temporarily unavailable. Please try again in a minute.'
      : label === 'image'
        ? 'Image upload is temporarily unavailable. Please try again in a minute.'
        : 'File upload is temporarily unavailable. Please try again in a minute.';
  }
  return `We couldn't upload the ${selected}. Please try again or choose a ${alternate}.`;
}

function initialActionState() {
  return {
    status: '',
    reasonCode: '',
    reasonNote: '',
    overrideTransition: false,
    loading: false,
    error: '',
    success: '',
    blockedByTransition: false,
  };
}

function getReasonCodesForAction(config, status) {
  if (!config) return [];
  if (config.reasonCodesByStatus && status) {
    return config.reasonCodesByStatus[status] || [];
  }
  return config.reasonCodes || [];
}

function Field({ label, value, help }) {
  return (
    <div className="ops-kv">
      <div className="k">
        {label}
        {help ? (
          <span className="ops-inline-help" title={help} aria-label={help}>?</span>
        ) : null}
      </div>
      <div className="v">{value ?? '—'}</div>
    </div>
  );
}

function recognitionLabel(value) {
  switch (String(value || '').trim()) {
    case 'irs_determination_letter':
      return 'IRS determination letter';
    case 'parent_group_exemption':
      return 'Covered under a parent organization’s group exemption / group ruling';
    case 'religious_no_separate_letter':
      return 'Church or religious organization without a separate IRS determination letter';
    case 'government_unit_public_entity':
      return 'Government unit / public entity';
    case 'other_not_sure':
      return 'Other / Not sure';
    default:
      return value || '—';
  }
}

function organizationTypeLabel(value) {
  switch (String(value || '').trim()) {
    case 'independent':
      return 'Independent';
    case 'chapter_affiliate':
      return 'Chapter/Affiliate';
    case 'umbrella_parent':
      return 'Umbrella/Parent';
    case 'supporting_509a3':
      return '509(a)(3) Supporting Org';
    case 'religious_gen':
      return 'Religious / Faith-Based Organization';
    case 'edu_health_gov':
      return 'Education / Health / Government Affiliated';
    default:
      return value || '—';
  }
}

function isImageUrl(url = '') {
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/i.test(url);
}

function cleanAssetUrl(url = '') {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (!/^https?:\/\//i.test(raw)) return raw.split('?')[0];
  try {
    const parsed = new URL(raw);
    return `${parsed.origin}${decodeURIComponent(parsed.pathname)}`;
  } catch (_err) {
    return raw.split('?')[0];
  }
}

async function inferHeroModeFromFile(file) {
  if (!file || typeof URL === 'undefined') return 'photo';
  const objectUrl = URL.createObjectURL(file);
  try {
    const meta = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({});
      img.src = objectUrl;
    });
    return defaultHeroModeForImageMeta(meta);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function isPdfUrl(url = '') {
  // fix/storage-routes-issue: keep PDF detection resilient for signed CDN/S3 URLs.
  const raw = String(url || '').trim();
  if (!raw) return false;
  if (/\.pdf(\?|#|$)/i.test(raw)) return true;
  try {
    const parsed = new URL(raw);
    const path = decodeURIComponent(parsed.pathname || '');
    if (/\.pdf$/i.test(path)) return true;
    const responseContentType = (parsed.searchParams.get('response-content-type') || '').toLowerCase();
    if (responseContentType.includes('application/pdf')) return true;
    const contentType = (parsed.searchParams.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/pdf')) return true;
  } catch (_err) {
    // ignore parse failures
  }
  return false;
}

function formatMoney(cents) {
  const n = Number(cents || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })
    .format(n / 100);
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function charCountLabel(value, max) {
  return `${String(value || '').length}/${max}`;
}

function formatAuditValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_err) {
      return String(value);
    }
  }
  return String(value);
}

function titleCaseWords(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const GEOGRAPHY_REASON_LABELS = {
  google_no_results: 'Google could not verify the root/HQ geography',
  root_geography_unresolved: 'Root/HQ geography needs manual confirmation',
  unresolved_geography: 'Root/HQ geography needs manual confirmation',
  canonical_market_missing: 'No canonical market was matched',
  ambiguous_geography: 'Multiple possible geography matches',
  out_of_footprint: 'Marked intentionally out-of-footprint',
  ops_resolved_to_canonical_market: 'Resolved manually by Ops',
  canonical_market_resolved: 'Resolved to a canonical market',
};

function formatGeographyStatusLabel(status, { hasRootMarket = false } = {}) {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'resolved') return 'Verified';
  if (normalized === 'ambiguous') return 'Ambiguous - Ops Review Required';
  if (normalized === 'needs_ops_review') return 'Ops Review Required';
  if (hasRootMarket) return 'Not Verified - Market Key Exists';
  return 'Unresolved - Ops Review Required';
}

function formatGeographyReasonLabel(reasonCode) {
  const normalized = String(reasonCode || '').trim().toLowerCase();
  return GEOGRAPHY_REASON_LABELS[normalized] || titleCaseWords(reasonCode || '') || '—';
}

function getNfpGeographyReviewState(nfp = {}) {
  const geography = nfp?.geography || {};
  const resolution = geography?.geographyResolution || {};
  const rootMarketKey = String(geography?.marketKey || '').trim();
  const hqMarketKey = String(geography?.hq?.marketKey || '').trim();
  const serviceAreaKeys = Array.isArray(geography?.serviceAreaKeys) ? geography.serviceAreaKeys.filter(Boolean) : [];
  const status = String(resolution?.status || '').trim().toLowerCase();
  const reasonCode = String(resolution?.reasonCode || '').trim().toLowerCase();
  const hasRootMarket = !!(rootMarketKey || hqMarketKey);
  const needsReview = status !== 'resolved' || !hasRootMarket;
  const hasResolvedServiceAreas = serviceAreaKeys.length > 0;

  let guidance = 'Root/HQ geography is verified. No geography action is required.';
  if (needsReview && hasRootMarket && reasonCode === 'google_no_results') {
    guidance = 'A canonical root market is present, but Google did not verify the HQ/root geography. If the market is correct, use Resolve Geography to manually confirm it.';
  } else if (needsReview && hasRootMarket) {
    guidance = 'A canonical root market is present, but root/HQ geography has not been manually verified. Confirm the market with Resolve Geography or retry Google resolution if the address data changed.';
  } else if (needsReview && hasResolvedServiceAreas) {
    guidance = 'Service areas are resolved, but the NFP root/HQ geography is not. Resolve the root market or mark it intentionally out-of-footprint.';
  } else if (needsReview) {
    guidance = 'Root/HQ geography is unresolved. Retry Google resolution, resolve to a canonical market, or mark intentionally out-of-footprint.';
  }

  return {
    needsReview,
    hasRootMarket,
    rootMarketKey,
    hqMarketKey,
    statusLabel: formatGeographyStatusLabel(status, { hasRootMarket }),
    reasonLabel: formatGeographyReasonLabel(reasonCode),
    guidance,
    serviceAreaHelp: hasResolvedServiceAreas
      ? 'Service areas indicate where this NFP serves consumers. They do not verify the NFP root/HQ geography.'
      : 'No resolved service-area coverage is currently attached to this NFP.',
  };
}

const NFP_BLOCKER_LABELS = {
  nfp_not_active: 'NFP Not Active',
  content_restricted: 'Content Restricted',
  proof_rejected: 'Proof Rejected',
  connected_account_missing: 'Connected Account Missing',
  connected_account_incomplete: 'Connected Account Incomplete',
  transfers_not_enabled: 'Transfers Not Enabled',
  payouts_paused: 'Payouts Paused',
  recent_payout_failed: 'Recent Payout Failed',
};

function formatNfpBlockerCode(code) {
  return NFP_BLOCKER_LABELS[code] || titleCaseWords(code || '');
}

function collectNfpRecommendedActions(blockers = []) {
  const seen = new Set();
  const actions = [];
  (Array.isArray(blockers) ? blockers : []).forEach((blocker) => {
    const list = Array.isArray(blocker?.actions) ? blocker.actions : [];
    list.forEach((action) => {
      const key = String(action?.key || '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      actions.push(action);
    });
  });
  return actions;
}

function makeImpactDraftRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row, idx) => ({
    id: `${row?.segment || 'individual'}-${idx}-${Math.random().toString(36).slice(2, 8)}`,
    segment: row?.segment === 'community' ? 'community' : 'individual',
    thresholdDollars: Number(row?.thresholdCents || 0) / 100,
    description: String(row?.description || ''),
    evidenceNote: String(row?.evidenceNote || ''),
  }));
}

function AssetPreview({ label, url, variant = 'default' }) {
  if (!url) return null;
  const isLogo = variant === 'logo';
  const isHero = variant === 'hero';
  const cardStyle = isLogo ? { maxWidth: 360 } : (isHero ? { maxWidth: 560 } : undefined);
  const imageStyle = {
    width: '100%',
    height: isLogo ? 110 : (isHero ? 'auto' : 120),
    maxHeight: isHero ? 320 : undefined,
    objectFit: isLogo || isHero ? 'contain' : 'cover',
    display: 'block',
    background: '#f7fbfa',
  };
  return (
    <div className="ops-media-card" style={{ marginBottom: 10, ...cardStyle }}>
      {isImageUrl(url) ? (
        <img src={url} alt={label} style={imageStyle} />
      ) : isPdfUrl(url) ? (
        <iframe
          src={url}
          title={label}
          style={{ width: '100%', height: isLogo ? 120 : 220, border: 0, display: 'block' }}
        />
      ) : (
        <div className="label" style={{ paddingTop: 12 }}>
          Preview unavailable for this file type.
        </div>
      )}
      <div className="label">{label}</div>
      <div className="label">
        <a href={url} download>Download</a>
      </div>
    </div>
  );
}

function HealthChip({ label, value, tone = '', onEdit, title }) {
  return (
    <button
      type="button"
      className={`ops-chip ${tone}`}
      onClick={onEdit || undefined}
      disabled={!onEdit}
      title={title || ''}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        opacity: onEdit ? 1 : 0.95,
        cursor: onEdit ? 'pointer' : 'default',
        border: 'none',
      }}
    >
      <span>{label}: {value}</span>
      {onEdit ? <span aria-hidden="true">✎</span> : null}
    </button>
  );
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 1000,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          overflow: 'auto',
          background: '#ffffff',
          borderRadius: 18,
          padding: 20,
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <h3 className="ops-section-title" style={{ margin: 0 }}>{title}</h3>
          <button type="button" className="ops-btn" onClick={onClose}>Close</button>
        </div>
        <div style={{ marginTop: 16 }}>{children}</div>
      </div>
    </div>
  );
}

export default function OpsNfpReview() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nfp, setNfp] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [proofUploadOpen, setProofUploadOpen] = useState(false);
  const [proofUploadType, setProofUploadType] = useState('letterOfDetermination');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [profilePreviewOpen, setProfilePreviewOpen] = useState(false);
  const [profilePreviewMode, setProfilePreviewMode] = useState('draft');
  const [impactModalOpen, setImpactModalOpen] = useState(false);
  const [impactDraftRows, setImpactDraftRows] = useState([]);
  const [impactSaving, setImpactSaving] = useState(false);
  const [impactError, setImpactError] = useState('');
  const [profileDraft, setProfileDraft] = useState({
    shortDescription: '',
    whySupportShort: '',
    heroImageUrl: '',
    heroMode: 'photo',
    editingShortDescription: false,
    editingWhySupportShort: false,
    loading: false,
    error: '',
  });
  const [imageEditor, setImageEditor] = useState({
    open: false,
    imageUrl: '',
    imageLabel: '',
    tagsText: '',
    isHero: false,
    doNotUse: false,
    isDeleted: false,
    loading: false,
    error: '',
  });
  const [modalActionKey, setModalActionKey] = useState('');
  const [consumedAutoAction, setConsumedAutoAction] = useState('');
  const [imageModal, setImageModal] = useState({
    open: false,
    imageUrl: '',
    imageLabel: '',
    reasonCode: '',
    reasonNote: '',
    loading: false,
    error: '',
  });
  const [actions, setActions] = useState({
    proof: initialActionState(),
    moderation: initialActionState(),
    registration: initialActionState(),
    causeStatus: initialActionState(),
    addNote: initialActionState(),
  });

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/ops/nfps/${encodeURIComponent(id)}`);
      setNfp(data?.nfp || null);
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        clearAuthToken();
        navigate('/login', { replace: true });
        return;
      }
      setError(err?.data?.error || err?.message || 'Failed to load NFP review detail');
      setNfp(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const timer = setTimeout(() => setToastMessage(''), 2500);
    return () => clearTimeout(timer);
  }, [toastMessage]);

  useEffect(() => {
    const action = String(searchParams.get('action') || '');
    if (!action || !ACTION_CONFIG[action]) return;
    const consumeKey = `${id}:${action}`;
    if (consumedAutoAction === consumeKey) return;
    setConsumedAutoAction(consumeKey);
    const currentStatus =
      action === 'proof' ? (nfp?.summary?.proofVerificationStatus || '') :
      action === 'moderation' ? (nfp?.summary?.moderationStatus || '') :
      action === 'registration' ? (nfp?.summary?.registrationStatus || '') :
      action === 'causeStatus' ? (nfp?.summary?.causeStatus || '') :
      action === 'addNote' ? 'note' : '';
    setActions((prev) => ({
      ...prev,
      [action]: {
        ...(prev[action] || initialActionState()),
        status: prev[action]?.status || currentStatus,
        error: '',
        success: '',
        blockedByTransition: false,
      },
    }));
    setModalActionKey(action);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [consumedAutoAction, id, searchParams, setSearchParams, nfp]);

  const headerChips = useMemo(() => {
    if (!nfp) return [];
    return [
      { label: `Registration: ${nfp.summary?.registrationStatus || '—'}`, tone: 'info' },
      { label: `Proof: ${nfp.summary?.proofVerificationStatus || '—'}`, tone: 'info' },
      { label: `Moderation: ${nfp.summary?.moderationStatus || '—'}`, tone: 'info' },
      { label: `Cause Status: ${nfp.summary?.causeStatus || '—'}`, tone: 'info' },
      { label: `Live Ready: ${nfp.readiness?.visibilityReady ? 'Yes' : 'No'}`, tone: nfp.readiness?.visibilityReady ? 'good' : 'bad' },
      { label: `Payout Ready: ${nfp.readiness?.payoutReady ? 'Yes' : 'No'}`, tone: nfp.readiness?.payoutReady ? 'good' : 'bad' },
      { label: `Connected Account: ${nfp.payoutPanel?.stripeConnected ? 'Connected' : 'Not connected'}`, tone: nfp.payoutPanel?.stripeConnected ? 'good' : 'bad' },
      { label: `Transfers: ${nfp.payoutPanel?.stripeTransfersCapability || 'inactive'}`, tone: nfp.payoutPanel?.stripeTransfersCapability === 'active' ? 'good' : 'bad' },
    ];
  }, [nfp]);

  function updateActionState(actionKey, patch) {
    setActions((prev) => ({
      ...prev,
      [actionKey]: { ...prev[actionKey], ...patch },
    }));
  }

  function getCurrentStatusValue(actionKey) {
    if (!nfp) return '';
    if (actionKey === 'proof') return nfp.summary?.proofVerificationStatus || '';
    if (actionKey === 'moderation') return nfp.summary?.moderationStatus || '';
    if (actionKey === 'registration') return nfp.summary?.registrationStatus || '';
    if (actionKey === 'causeStatus') return nfp.summary?.causeStatus || '';
    if (actionKey === 'addNote') return 'note';
    return '';
  }

  function openActionModal(actionKey) {
    updateActionState(actionKey, {
      status: actionKey === 'addNote' ? 'note' : (actions[actionKey]?.status || getCurrentStatusValue(actionKey)),
      reasonNote: actionKey === 'addNote' ? '' : actions[actionKey]?.reasonNote || '',
      error: '',
      success: '',
      blockedByTransition: false,
    });
    setModalActionKey(actionKey);
  }

  function closeActionModal() {
    setModalActionKey('');
  }

  async function uploadViaPresign(file, kind) {
    // fix/storage-routes-issue: backend upload router is mounted at /api/uploads/*
    if (file.size > MAX_UPLOAD_BYTES) {
      const prefix = kind === 'logo' || kind === 'image' ? 'Image' : 'File';
      throw new Error(`${prefix} too large. Please choose a file that is ${formatUploadLimitLabel(MAX_UPLOAD_BYTES)} or smaller.`);
    }

    let presign;
    try {
      presign = await apiFetch('/uploads/presign', {
        method: 'POST',
        body: {
          kind,
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
        },
      });
    } catch (err) {
      throw new Error(friendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    if (!presign?.uploadUrl) {
      throw new Error(friendlyUploadError('Missing upload URL from server', { kind }));
    }

    let putRes;
    try {
      putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
    } catch (err) {
      throw new Error(friendlyUploadError(err?.message, { kind }));
    }
    if (!putRes.ok) {
      const raw = await putRes.text().catch(() => '');
      throw new Error(friendlyUploadError(raw || `Upload failed (${putRes.status})`, { kind }));
    }

    try {
      await apiFetch('/uploads/complete', {
        method: 'POST',
        body: {
          key: presign.key,
          fileUrl: presign.fileUrl,
          kind,
        },
      });
    } catch (err) {
      throw new Error(friendlyUploadError(err?.data?.error || err?.message, { kind }));
    }

    return {
      url: presign.fileUrl,
      alt: file.name,
      mime: file.type || '',
      size: file.size || 0,
    };
  }

  async function saveProfileAssets(payload) {
    const marketKey = String(
      payload?.marketKey ||
      nfp?.marketKey ||
      nfp?.summary?.marketKey ||
      ''
    ).trim();
    await apiFetch(`/ops/nfps/${encodeURIComponent(id)}/profile-assets`, {
      method: 'PUT',
      body: {
        ...payload,
        ...(marketKey ? { marketKey } : {}),
      },
    });
    await load();
  }

  function openImpactModal() {
    setImpactDraftRows(makeImpactDraftRows(nfp?.impactMilestones || []));
    setImpactError('');
    setImpactSaving(false);
    setImpactModalOpen(true);
  }

  function closeImpactModal() {
    setImpactModalOpen(false);
    setImpactDraftRows([]);
    setImpactError('');
    setImpactSaving(false);
  }

  function addImpactRow(segment) {
    setImpactDraftRows((prev) => ([
      ...prev,
      {
        id: `${segment}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        segment: segment === 'community' ? 'community' : 'individual',
        thresholdDollars: 0,
        description: '',
        evidenceNote: '',
      },
    ]));
  }

  function updateImpactRow(rowId, patch) {
    setImpactDraftRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, ...patch } : row)));
  }

  function removeImpactRow(rowId) {
    setImpactDraftRows((prev) => prev.filter((row) => row.id !== rowId));
  }

  async function saveImpactMilestones() {
    const endpoint = nfp?.adminActions?.endpoints?.impactMilestones;
    if (!endpoint) {
      setImpactError('Impact milestones endpoint unavailable');
      return;
    }

    const cleaned = impactDraftRows
      .map((row) => {
        const thresholdDollars = Number(row.thresholdDollars);
        return {
          segment: row.segment === 'community' ? 'community' : 'individual',
          thresholdCents: Number.isFinite(thresholdDollars) ? Math.max(0, Math.round(thresholdDollars * 100)) : 0,
          description: String(row.description || '').trim().slice(0, 300),
          evidenceNote: String(row.evidenceNote || '').trim().slice(0, 300),
        };
      })
      .filter((row) => row.description);

    setImpactSaving(true);
    setImpactError('');
    try {
      await apiFetch(endpoint, {
        method: 'PUT',
        body: { milestones: cleaned },
      });
      setToastMessage('Updated');
      closeImpactModal();
      await load();
    } catch (err) {
      setImpactSaving(false);
      setImpactError(err?.data?.error || err?.message || 'Failed to update impact levels');
    }
  }

  function openProfilePreview() {
    const draft = nfp?.profileModeration?.previewDraft || {};
    const liveProfile = nfp?.profileModeration?.liveProfile || {};
    const proposedProfile = nfp?.profileModeration?.proposedProfile || {};
    setProfileDraft({
      shortDescription: draft.shortDescription || '',
      whySupportShort: draft.whySupportShort || '',
      heroImageUrl: cleanAssetUrl(
        draft.heroImageUrl ||
        proposedProfile.heroImageUrl ||
        liveProfile.heroImageUrl ||
        nfp?.profileModeration?.heroImageUrl ||
        ''
      ),
      heroMode: normalizeHeroMode(
        draft.heroMode ||
        proposedProfile.heroMode ||
        liveProfile.heroMode ||
        nfp?.profileModeration?.heroMode
      ),
      editingShortDescription: false,
      editingWhySupportShort: false,
      loading: false,
      error: '',
    });
    setProfilePreviewMode(nfp?.profileModeration?.hasProposedChangesToReview ? 'proposed' : 'draft');
    setProfilePreviewOpen(true);
  }

  function closeProfilePreview() {
    setProfilePreviewOpen(false);
    setProfilePreviewMode('draft');
    setProfileDraft({
      shortDescription: '',
      whySupportShort: '',
      heroImageUrl: '',
      heroMode: 'photo',
      editingShortDescription: false,
      editingWhySupportShort: false,
      loading: false,
      error: '',
    });
  }

  async function saveProfilePreview(setLiveProfile = false) {
    const shortDescription = String(profileDraft.shortDescription || '').trim();
    const whySupportShort = String(profileDraft.whySupportShort || '').trim();
    if (shortDescription.length > 180) {
      setProfileDraft((prev) => ({ ...prev, error: 'Short description must be 180 characters or fewer.' }));
      return;
    }
    if (whySupportShort.length > 800) {
      setProfileDraft((prev) => ({ ...prev, error: 'Why Support text must be 800 characters or fewer.' }));
      return;
    }
    if (!profileDraft.heroImageUrl) {
      setProfileDraft((prev) => ({ ...prev, error: 'Select a hero image before saving this preview.' }));
      return;
    }
    if (setLiveProfile && !window.confirm('Are you sure?')) {
      return;
    }
    setProfileDraft((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      await saveProfileAssets({
        previewShortDescription: shortDescription,
        previewWhySupportShort: whySupportShort,
        heroImageUrl: cleanAssetUrl(profileDraft.heroImageUrl),
        heroMode: normalizeHeroMode(profileDraft.heroMode),
        setLiveProfile,
      });
      setToastMessage('Updated');
      if (setLiveProfile) {
        closeProfilePreview();
      } else {
        setProfileDraft((prev) => ({
          ...prev,
          loading: false,
          error: '',
          editingShortDescription: false,
          editingWhySupportShort: false,
        }));
      }
    } catch (err) {
      setProfileDraft((prev) => ({
        ...prev,
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to save profile preview',
      }));
    }
  }

  function openImageEditor(img, idx) {
    setImageEditor({
      open: true,
      imageUrl: img?.url || '',
      imageLabel: img?.alt || `NFP image ${idx + 1}`,
      tagsText: Array.isArray(img?.tags) ? img.tags.join(', ') : '',
      isHero: !!img?.isHero,
      doNotUse: !!img?.doNotUse,
      isDeleted: !!img?.isDeleted,
      loading: false,
      error: '',
    });
  }

  function closeImageEditor() {
    setImageEditor({
      open: false,
      imageUrl: '',
      imageLabel: '',
      tagsText: '',
      isHero: false,
      doNotUse: false,
      isDeleted: false,
      loading: false,
      error: '',
    });
  }

  async function saveImageTags() {
    if (!imageEditor.imageUrl) return;
    setImageEditor((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      await saveProfileAssets({
        imageGovernance: [{
          url: imageEditor.imageUrl,
          tags: imageEditor.tagsText,
        }],
      });
      setToastMessage('Updated');
      setImageEditor((prev) => ({ ...prev, loading: false, error: '' }));
    } catch (err) {
      setImageEditor((prev) => ({
        ...prev,
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to save image tags',
      }));
    }
  }

  async function toggleImageDeleted(nextDeleted) {
    if (!imageEditor.imageUrl) return;
    setImageEditor((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      await saveProfileAssets({
        imageGovernance: [{
          url: imageEditor.imageUrl,
          isDeleted: nextDeleted,
        }],
      });
      setToastMessage('Updated');
      closeImageEditor();
    } catch (err) {
      setImageEditor((prev) => ({
        ...prev,
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to update image availability',
      }));
    }
  }

  function openDoNotUseModal(img) {
    setImageModal({
      open: true,
      imageUrl: img?.url || '',
      imageLabel: img?.alt || 'Selected image',
      reasonCode: img?.doNotUseReasonCode || '',
      reasonNote: img?.doNotUseReasonNote || '',
      loading: false,
      error: '',
    });
  }

  function closeDoNotUseModal() {
    setImageModal({
      open: false,
      imageUrl: '',
      imageLabel: '',
      reasonCode: '',
      reasonNote: '',
      loading: false,
      error: '',
    });
  }

  async function saveDoNotUseFlag() {
    if (!imageModal.imageUrl) return;
    if (!imageModal.reasonCode) {
      setImageModal((prev) => ({ ...prev, error: 'Reason is required.' }));
      return;
    }
    if (imageModal.reasonCode === 'other' && !String(imageModal.reasonNote || '').trim()) {
      setImageModal((prev) => ({ ...prev, error: 'Reason note is required when reason is other.' }));
      return;
    }
    setImageModal((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      await saveProfileAssets({
        imageGovernance: [{
          url: imageModal.imageUrl,
          doNotUse: true,
          doNotUseReasonCode: imageModal.reasonCode,
          doNotUseReasonNote: imageModal.reasonNote,
        }],
      });
      setToastMessage('Updated');
      closeDoNotUseModal();
    } catch (err) {
      setImageModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.data?.error || err?.message || 'Failed to update image usage',
      }));
    }
  }

  async function clearDoNotUseFlag(img) {
    if (!img?.url) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      await saveProfileAssets({
        imageGovernance: [{
          url: img.url,
          doNotUse: false,
        }],
      });
      setToastMessage('Updated');
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to clear image restriction');
    } finally {
      setUploadBusy(false);
    }
  }

  async function onLogoSelected(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      const uploaded = await uploadViaPresign(file, 'logo');
      await saveProfileAssets({ addOpsLogos: [uploaded] });
      setToastMessage('Updated');
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to upload logo');
    } finally {
      setUploadBusy(false);
      event.target.value = '';
    }
  }

  async function setPrimaryOpsLogo(logoUrl) {
    if (!logoUrl) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      await saveProfileAssets({ setPrimaryOpsLogoUrl: logoUrl });
      setToastMessage('Updated');
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to set primary logo');
    } finally {
      setUploadBusy(false);
    }
  }

  async function toggleOpsLogoDeleted(logoUrl, nextDeleted) {
    if (!logoUrl) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      await saveProfileAssets({ logoGovernance: [{ url: logoUrl, isDeleted: nextDeleted }] });
      setToastMessage('Updated');
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to update logo availability');
    } finally {
      setUploadBusy(false);
    }
  }

  async function onImagesSelected(event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      const uploaded = [];
      for (const file of files) {
        const heroMode = await inferHeroModeFromFile(file);
        // eslint-disable-next-line no-await-in-loop
        uploaded.push({ ...(await uploadViaPresign(file, 'image')), heroMode });
      }
      await saveProfileAssets({ addImages: uploaded });
      setToastMessage(`${uploaded.length === 1 ? 'Image' : `${uploaded.length} images`} uploaded`);
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to upload images');
    } finally {
      setUploadBusy(false);
      event.target.value = '';
    }
  }

  async function onProofSelected(event, proofField, proofType) {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadBusy(true);
    setUploadError('');
    try {
      const uploaded = await uploadViaPresign(file, 'proof');
      await saveProfileAssets({
        proof: {
          [proofField]: uploaded.url,
          type: proofType,
        },
      });
      setProofUploadOpen(false);
    } catch (err) {
      setUploadError(err?.data?.error || err?.message || 'Failed to upload proof document');
    } finally {
      setUploadBusy(false);
      event.target.value = '';
    }
  }

  const proofUploadConfig = {
    letterOfDetermination: { field: 'letterOfDeterminationUrl', proofType: 'lod' },
    operatingUnderGenDoc: { field: 'operatingUnderGenDocUrl', proofType: 'operating_under_gen' },
    governmentUnitDoc: { field: 'governmentUnitDocUrl', proofType: 'government_unit' },
    chapterAuthorization: { field: 'chapterAuthorizationUrl', proofType: 'operating_under_gen' },
    otherSupportingDoc: { field: 'otherSupportingDocUrl', proofType: 'other_supporting' },
  };

  function onProofModalFileSelected(event) {
    const cfg = proofUploadConfig[proofUploadType];
    if (!cfg) return;
    onProofSelected(event, cfg.field, cfg.proofType);
  }

  function runBlockerAction(actionKey) {
    if (['proof', 'moderation', 'registration', 'causeStatus'].includes(actionKey)) {
      openActionModal(actionKey);
    }
  }

  async function runAction(actionKey) {
    const current = actions[actionKey];
    const config = ACTION_CONFIG[actionKey];
    const endpoint = nfp?.adminActions?.endpoints?.[config.endpointKey];
    if (!endpoint) {
      updateActionState(actionKey, { error: 'Action endpoint unavailable' });
      return;
    }
    if (actionKey !== 'addNote' && !current.status) {
      updateActionState(actionKey, { error: 'Select a status first', success: '' });
      return;
    }
    if (actionKey === 'addNote' && !String(current.reasonNote || '').trim()) {
      updateActionState(actionKey, { error: 'Add a note before saving', success: '' });
      return;
    }

    const needsReason =
      (actionKey === 'proof' && current.status === 'rejected') ||
      (actionKey === 'moderation' && ['needs_action', 'rejected'].includes(current.status)) ||
      (actionKey === 'registration' && ['needs_action', 'rejected', 'withdrawn'].includes(current.status)) ||
      (actionKey === 'causeStatus' && current.status !== 'active');
    if (needsReason && !current.reasonCode) {
      updateActionState(actionKey, { error: 'Select a reason code before saving', success: '' });
      return;
    }
    if (current.reasonCode === 'other' && !String(current.reasonNote || '').trim()) {
      updateActionState(actionKey, { error: 'Add an audit note when the reason code is other', success: '' });
      return;
    }
    if (current.overrideTransition) {
      if (!current.reasonCode || !current.reasonNote) {
        updateActionState(actionKey, { error: 'reasonCode and reasonNote are required for override', success: '' });
        return;
      }
      const confirmed = window.confirm('Override a blocked transition? This will be audited.');
      if (!confirmed) return;
    }

    updateActionState(actionKey, { loading: true, error: '', success: '' });
    try {
      const payload = {
        status: current.status,
        reasonCode: current.reasonCode || undefined,
        reasonNote: current.reasonNote || undefined,
        overrideTransition: !!current.overrideTransition,
      };
      if (actionKey === 'proof' && current.status === 'rejected') {
        payload.rejectionReason = current.reasonNote || 'Rejected by ops reviewer';
      }
      if (actionKey === 'moderation') {
        payload.reason = current.reasonNote || undefined;
      }
      if (actionKey === 'addNote') {
        payload.note = String(current.reasonNote || '').trim();
      }
      await apiFetch(endpoint, { method: config.method || 'PUT', body: payload });
      setToastMessage('Updated');
      updateActionState(actionKey, {
        loading: false,
        success: 'Status updated',
        error: '',
        blockedByTransition: false,
        overrideTransition: false,
      });
      if (actionKey === 'addNote') {
        // keep the audit area relevant after a note is added
      }
      closeActionModal();
      await load();
    } catch (err) {
      if (err?.status === 409) {
        updateActionState(actionKey, {
          loading: false,
          blockedByTransition: true,
          error: err?.data?.error || 'Transition blocked',
          success: '',
        });
        return;
      }
      updateActionState(actionKey, {
        loading: false,
        error: err?.data?.error || err?.message || 'Action failed',
        success: '',
      });
    }
  }

  async function runGeographyAction(action) {
    const endpointBase = nfp?.adminActions?.endpoints?.geographyResolution;
    if (!nfp?.adminActions?.canManageMarketOperations || !endpointBase) return;

    let endpoint = endpointBase;
    let method = 'PATCH';
    let body = {};

    if (action === 'retry') {
      endpoint += '/retry';
      method = 'POST';
      body = {};
    } else if (action === 'out_of_footprint') {
      const reason = window.prompt('Reason for marking this NFP root geography intentionally out-of-footprint?');
      if (reason === null) return;
      endpoint += '/out-of-footprint';
      body = { reason: String(reason || '').trim() || 'Marked intentionally out-of-footprint by Ops' };
    } else if (action === 'resolve') {
      const marketKey = window.prompt('Canonical market key to resolve this NFP root geography to?');
      if (!marketKey) return;
      const note = window.prompt('Resolution note?') || '';
      endpoint += '/resolve';
      body = { marketKey: String(marketKey).trim(), note: String(note).trim() || undefined };
    } else {
      return;
    }

    setToastMessage('');
    try {
      await apiFetch(endpoint, { method, body });
      setToastMessage('Geography updated');
      await load();
    } catch (err) {
      setToastMessage(err?.data?.error || err?.message || 'Failed to update geography');
    }
  }

  const geographyReview = getNfpGeographyReviewState(nfp);

  if (loading) {
    return (
      <OpsLayout title="NFP Review" subtitle="Loading NFP detail...">
        <div className="ops-nfp-content">Loading NFP detail…</div>
      </OpsLayout>
    );
  }

  if (!nfp) {
    return (
      <OpsLayout
        title="NFP Review"
        subtitle={error || 'NFP not found'}
        headerActions={(
          <button type="button" className="ops-btn primary" onClick={() => navigate('/ops/nfps')}>
            Back
          </button>
        )}
      />
    );
  }

  const hiddenReasons = nfp?.readiness?.hiddenReasons || [];
  const geographyFocused = searchParams.get('action') === 'geography';
  const proofDocs = nfp?.profileModeration?.proof || {};
  const orgStructure = nfp?.registrationEssentials?.orgStructure || '';
  const hasLocalEIN = nfp?.registrationEssentials?.hasLocalEIN;
  const parentEin = nfp?.registrationEssentials?.parentEin;
  const parentEinRequired =
    ['chapter_affiliate', 'religious_gen', 'edu_health_gov'].includes(orgStructure) &&
    hasLocalEIN === false;
  const parentEinDisplay = parentEin
    ? parentEin
    : parentEinRequired
      ? <span className="ops-chip bad">Not provided</span>
      : 'N/A';
  const proofDocItems = [
    { key: 'lod', label: 'Letter of Determination', url: proofDocs.letterOfDeterminationUrl },
    { key: 'operating', label: 'Parent Exemption / Group Ruling Document', url: proofDocs.operatingUnderGenDocUrl },
    { key: 'gov', label: 'Government Unit Document', url: proofDocs.governmentUnitDocUrl },
    { key: 'chapter', label: 'Parent Recognition / Coverage Proof', url: proofDocs.chapterAuthorizationUrl },
    { key: 'other', label: 'Other Supporting Documentation', url: proofDocs.otherSupportingDocUrl },
  ].filter((x) => !!x.url);
  const headerMetrics = nfp?.headerMetrics || {};
  const subtitle = (
    <>
      <div>{`Cause ID: ${nfp.causeId || '—'} | Registration ID: ${nfp.registrationId || '—'} | # of supporters: ${Number(headerMetrics.supportersCount || 0).toLocaleString()}`}</div>
      <div>
        {[
          `Active Since: ${formatDate(headerMetrics.activeSinceAt)}`,
          `Contributions to Date: ${formatMoney(headerMetrics.contributionsToDateCents)}`,
          `Disbursed $ to Date: ${formatMoney(headerMetrics.disbursedToDateCents)}`,
          `Next Payment Amount $: ${formatMoney(headerMetrics.nextPaymentAmountCents)}`,
          `Not Eligible this payout $: ${formatMoney(headerMetrics.notEligibleThisPayoutCents)}`,
        ].join(' | ')}
      </div>
    </>
  );
  const modalConfig = ACTION_CONFIG[modalActionKey] || null;
  const modalState = modalActionKey ? actions[modalActionKey] : null;
  const liveBlockers = Array.isArray(nfp?.blockers?.live) ? nfp.blockers.live : [];
  const payoutBlockers = Array.isArray(nfp?.blockers?.payout) ? nfp.blockers.payout : [];
  const liveRecommendedActions = collectNfpRecommendedActions(liveBlockers);
  const payoutRecommendedActions = collectNfpRecommendedActions(payoutBlockers);

  return (
    <OpsLayout
      title={nfp.name || 'NFP Review'}
      subtitle={subtitle}
      headerActions={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" className="ops-btn" onClick={() => openActionModal('addNote')} title="Add an audit note.">
            Add Note
          </button>
          <button type="button" className="ops-btn primary" onClick={() => navigate('/ops/nfps')}>
            Back
          </button>
        </div>
      )}
    >
      <div className="ops-nfp-content">
      {toastMessage ? <div className="ops-message" style={{ marginTop: 0, marginBottom: 12 }}>{toastMessage}</div> : null}
      <div className="ops-nfp-tabs" style={{ marginTop: 0 }}>
        {headerChips.map((chip) => (
          <span key={chip.label} className={`ops-chip ${chip.tone || ''}`}>
            {chip.label}
          </span>
        ))}
      </div>

      {nfp.adminActions?.canManageNfpStatuses ? (
        <section className="ops-nfp-panel" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <HealthChip
              label="Registration"
              value={nfp.summary?.registrationStatus || '—'}
              onEdit={() => openActionModal('registration')}
              title="Change registration status."
            />
            <HealthChip
              label="Proof"
              value={nfp.summary?.proofVerificationStatus || '—'}
              onEdit={() => openActionModal('proof')}
              title="Change proof status."
            />
            <HealthChip
              label="Moderation"
              value={nfp.summary?.moderationStatus || '—'}
              onEdit={() => openActionModal('moderation')}
              title="Change moderation status."
            />
            <HealthChip
              label="Cause Status"
              value={nfp.summary?.causeStatus || '—'}
              onEdit={() => openActionModal('causeStatus')}
              title="Change cause status."
            />
          </div>
        </section>
      ) : null}

      <section className="ops-nfp-panel" style={{ marginTop: 14 }}>
        <h3 className="ops-section-title">Readiness Summary</h3>
        <div style={{ display: 'grid', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginBottom: 6 }}>Live blockers</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {liveBlockers.length === 0 ? (
                <span className="ops-chip info">None</span>
              ) : liveBlockers.map((blocker, idx) => {
                const directAction = Array.isArray(blocker?.actions)
                  ? blocker.actions.find((action) => action?.opsCanExecute && ['proof', 'moderation', 'registration', 'causeStatus'].includes(action.key))
                  : null;
                if (!directAction) {
                  return (
                    <span
                      key={`${blocker.code}:${idx}`}
                      className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                      title={`${formatNfpBlockerCode(blocker.code)}${blocker.causeCode ? ` - ${titleCaseWords(blocker.causeCode)}` : ''}`}
                    >
                      {formatNfpBlockerCode(blocker.code)}
                    </span>
                  );
                }
                return (
                  <button
                    key={`${blocker.code}:${idx}`}
                    type="button"
                    className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                    style={{ border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2 }}
                    onClick={() => runBlockerAction(directAction.key)}
                    title={`${formatNfpBlockerCode(blocker.code)}${blocker.causeCode ? ` - ${titleCaseWords(blocker.causeCode)}` : ''}`}
                  >
                    {formatNfpBlockerCode(blocker.code)}
                  </button>
                );
              })}
            </div>
            <div className="ops-kv">
              <div className="k">Recommended actions</div>
              <div className="v" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {liveRecommendedActions.length === 0 ? 'None' : liveRecommendedActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    className="ops-btn"
                    disabled={!action.opsCanExecute}
                    onClick={() => runBlockerAction(action.key)}
                    title={action.opsCanExecute ? `Run ${action.label}.` : 'No direct workflow is available for this action yet.'}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#475569', fontWeight: 700, marginBottom: 6 }}>Payout blockers</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {payoutBlockers.length === 0 ? (
                <span className="ops-chip info">None</span>
              ) : payoutBlockers.map((blocker, idx) => (
                <span
                  key={`${blocker.code}:${idx}`}
                  className={`ops-chip ${blocker.severity === 'Critical' || blocker.severity === 'High' ? 'bad' : ''}`}
                  title={`${formatNfpBlockerCode(blocker.code)}${blocker.causeCode ? ` - ${titleCaseWords(blocker.causeCode)}` : ''}`}
                >
                  {formatNfpBlockerCode(blocker.code)}
                </span>
              ))}
            </div>
            <div className="ops-kv">
              <div className="k">Recommended actions</div>
              <div className="v" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {payoutRecommendedActions.length === 0 ? 'None' : payoutRecommendedActions.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    className="ops-btn"
                    disabled={!action.opsCanExecute}
                    onClick={() => runBlockerAction(action.key)}
                    title={action.opsCanExecute ? `Run ${action.label}.` : 'This requires follow-up outside the current Ops workflow.'}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ops-nfp-columns-layout">
        <div className="ops-nfp-column">
          <article className="ops-nfp-panel" style={{ marginTop: 0 }}>
            <h3 className="ops-section-title">Readiness & Visibility</h3>
            <Field
              label="Registration Status"
              value={(
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="ops-chip">
                    {nfp.summary?.registrationStatus || '—'}
                  </span>
                  <span style={{ color: '#5e7c82', fontSize: 12 }}>
                    submitted: {formatDateTime(nfp.summary?.registrationSubmittedAt)}
                  </span>
                </span>
              )}
            />
            <Field
              label="Moderation Status"
              value={(
                <span className="ops-chip">
                  {nfp.summary?.moderationStatus || '—'}
                </span>
              )}
            />
            <Field label="Ready for Moderation" value={nfp.readiness?.moderationReady ? 'Yes' : 'No'} />
            <Field
              label="Live in App"
              value={(
                <span className={`ops-chip ${nfp.readiness?.visibilityReady ? 'good' : 'bad'}`}>
                  {nfp.readiness?.visibilityReady ? 'Yes' : 'No'}
                </span>
              )}
            />
            <Field label="Payout Ready" value={String(!!nfp.readiness?.payoutReady)} />
            <Field label="Discovery Blockers" value={hiddenReasons.length ? hiddenReasons.join(', ') : 'none'} />
          </article>

          <article className="ops-nfp-panel">
            <h3 className="ops-section-title">Profile / Moderation</h3>
            {uploadError && <div className="ops-message error">{uploadError}</div>}
            <Field label="Logo present" value={String(!!nfp.profileModeration?.logoPresent)} />
            <Field label="Active logo source" value={nfp.profileModeration?.logoSource || 'none'} />
            <AssetPreview label="Active Logo" url={nfp.profileModeration?.logoUrl} variant="logo" />
            <AssetPreview label="Original NFP Logo" url={nfp.profileModeration?.nfpLogoUrl} variant="logo" />
            <div className="ops-nfp-field" style={{ marginBottom: 10 }}>
              <label>Upload Ops Logo</label>
              <input type="file" accept="image/*" disabled={uploadBusy} onChange={onLogoSelected} />
            </div>
            <Field label="Ops logos" value={nfp.profileModeration?.opsLogoCount} />
            <Field label="Deleted Ops logos" value={nfp.profileModeration?.deletedOpsLogoCount} />
            <div className="ops-media-grid" style={{ marginBottom: 12 }}>
              {(nfp.profileModeration?.opsLogos || []).map((logo, idx) => (
                <div key={`${logo.url || idx}`} className="ops-media-card">
                  <img src={logo.url} alt={logo.alt || `Ops logo ${idx + 1}`} />
                  <div className="label" style={{ display: 'grid', gap: 6 }}>
                    <div>
                      {logo.isPrimary ? <span className="ops-chip good" style={{ marginRight: 6 }}>Primary Ops Logo</span> : null}
                      {logo.isDeleted ? <span className="ops-chip">Soft Deleted</span> : null}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <a href={logo.url} download className="ops-btn" style={{ textDecoration: 'none' }}>Download</a>
                      <button
                        type="button"
                        className="ops-btn"
                        disabled={uploadBusy || logo.isDeleted || logo.isPrimary}
                        onClick={() => setPrimaryOpsLogo(logo.url)}
                        title="Set this Ops logo as the primary logo."
                      >
                        {logo.isPrimary ? 'Primary' : 'Set Primary'}
                      </button>
                      <button
                        type="button"
                        className="ops-btn"
                        disabled={uploadBusy}
                        onClick={() => toggleOpsLogoDeleted(logo.url, !logo.isDeleted)}
                        title={logo.isDeleted ? 'Restore this Ops logo.' : 'Soft delete this Ops logo.'}
                      >
                        {logo.isDeleted ? 'Restore' : 'Soft Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Field label="Image count" value={nfp.profileModeration?.imageCount} />
            <Field label="Deleted images" value={nfp.profileModeration?.deletedImageCount} />
            <Field
              label="Image set shown"
              value={nfp.profileModeration?.hasProposedChangesToReview ? 'NFP proposed images' : 'Live image set'}
            />
            <Field
              label="Hero image"
              value={nfp.profileModeration?.heroImageUrl ? 'Set' : 'Not set'}
            />
            <AssetPreview label="Current Hero Image" url={nfp.profileModeration?.heroImageUrl} variant="hero" />
            <Field label="Short description present" value={String(!!nfp.profileModeration?.shortDescription)} />
            {nfp.profileModeration?.hasProposedChangesToReview ? (
              <div className="ops-card-caption" style={{ marginBottom: 10, color: '#9a5b24' }}>
                The grid below is showing the NFP&apos;s current proposed image set. Compare it to the Current Live tab in Cause Detail Preview before publishing.
              </div>
            ) : null}
            <div className="ops-nfp-field" style={{ marginBottom: 10 }}>
              <label>Upload images</label>
              <input type="file" accept="image/*" multiple disabled={uploadBusy} onChange={onImagesSelected} />
            </div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="ops-btn primary"
                  style={{ background: '#e6a06f', borderColor: '#e6a06f', color: '#1c2a30' }}
                  disabled={uploadBusy}
                  onClick={openProfilePreview}
                  title="Preview and edit the cause profile as it would appear in the app."
                >
                  Cause Detail Preview
                </button>
                <button
                  type="button"
                  className="ops-btn"
                  disabled={uploadBusy}
                  onClick={openImpactModal}
                  title="Review and edit the impact levels entered by the NFP."
                >
                  Impact Levels
                </button>
              </div>
              {nfp.profileModeration?.hasProposedChangesToReview ? (
                <div className="ops-card-caption" style={{ marginTop: 6, color: '#9a5b24' }}>
                  Review needed: the NFP has proposed profile changes that are not live yet.
                </div>
              ) : null}
            </div>
            <div className="ops-media-grid">
              {(nfp.profileModeration?.images || []).map((img, idx) => (
                <div key={`${img.url || idx}`} className="ops-media-card">
                  <button
                    type="button"
                    onClick={() => openImageEditor(img, idx)}
                    style={{ padding: 0, border: 'none', background: 'transparent', width: '100%', cursor: 'pointer' }}
                    title="Open image review modal."
                  >
                    <div className="ops-media-frame">
                      {isImageUrl(img.url) ? (
                        <img src={img.url} alt={img.alt || `NFP image ${idx + 1}`} />
                      ) : isPdfUrl(img.url) ? (
                        <iframe
                          src={img.url}
                          title={img.alt || `NFP document ${idx + 1}`}
                          style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                        />
                      ) : (
                        <div className="ops-doc-thumb-file">FILE</div>
                      )}
                    </div>
                  </button>
                  <div className="label" style={{ display: 'grid', gap: 6 }}>
                    <div>
                      {img.isHero ? <span className="ops-chip good" style={{ marginRight: 6 }}>Hero image</span> : null}
                      {img.doNotUse ? <span className="ops-chip bad">Do not use</span> : null}
                      {img.isDeleted ? <span className="ops-chip" style={{ marginLeft: 6 }}>Soft deleted</span> : null}
                    </div>
                    {img.doNotUse ? (
                      <div style={{ fontSize: 12, color: 'var(--ops-muted, #5d7780)' }}>
                        {titleCaseWords(img.doNotUseReasonCode || 'other')}
                        {img.doNotUseReasonNote ? `: ${img.doNotUseReasonNote}` : ''}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <a href={img.url} download className="ops-btn" style={{ textDecoration: 'none' }}>Download</a>
                      <button
                        type="button"
                        className="ops-btn"
                        disabled={uploadBusy}
                        onClick={() => openImageEditor(img, idx)}
                        title="Open image review modal."
                      >
                        Review
                      </button>
                      {img.doNotUse ? (
                        <button
                          type="button"
                          className="ops-btn"
                          disabled={uploadBusy}
                          onClick={() => clearDoNotUseFlag(img)}
                          title="Allow this image to be used again."
                        >
                          Allow Use
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ops-btn"
                          disabled={uploadBusy}
                          onClick={() => openDoNotUseModal(img)}
                          title="Mark this image as do not use."
                        >
                          Mark Do Not Use
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Field label="whySupportShort" value={nfp.profileModeration?.whySupportShort} />
            <Field label="Profile copy preview" value={nfp.profileModeration?.profileCopyPreview} />
            <Field label="Mission/category tags" value={(nfp.profileModeration?.missionTags || []).join(', ') || '—'} />
            <Field label="Proof type" value={proofDocs.type || '—'} />
            <Field
              label="Proof docs"
              value={[
                proofDocs.letterOfDeterminationUrl ? 'letterOfDetermination' : null,
                proofDocs.operatingUnderGenDocUrl ? 'operatingUnderGenDoc' : null,
                proofDocs.governmentUnitDocUrl ? 'governmentUnitDoc' : null,
                proofDocs.chapterAuthorizationUrl ? 'chapterAuthorization' : null,
              ].filter(Boolean).join(', ') || 'none'}
            />
            <div className="ops-doc-thumb-grid">
              {proofDocItems.length === 0 && <div className="label">No proof docs uploaded.</div>}
              {proofDocItems.map((doc) => (
                <button
                  key={doc.key}
                  type="button"
                  className="ops-doc-thumb"
                  onClick={() => setPreviewAsset({ label: doc.label, url: doc.url })}
                  title={`Preview ${doc.label}`}
                >
                  {isImageUrl(doc.url) ? (
                    <img src={doc.url} alt={doc.label} />
                  ) : isPdfUrl(doc.url) ? (
                    <div className="ops-doc-thumb-file">PDF</div>
                  ) : (
                    <div className="ops-doc-thumb-file">FILE</div>
                  )}
                  <div className="ops-doc-thumb-label">
                    <div>{doc.label}</div>
                    <div style={{ fontSize: 11, opacity: 0.75 }}>
                      Click to preview
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                className="ops-btn"
                title="Upload a proof document."
                disabled={uploadBusy}
                onClick={() => setProofUploadOpen(true)}
              >
                Upload Proof Doc
              </button>
            </div>
          </article>
        </div>

        <div className="ops-nfp-column">
          <article className="ops-nfp-panel" style={{ marginTop: 0 }}>
            <h3 className="ops-section-title">Registration / Organization Essentials</h3>
            <Field label="Legal Name" value={nfp.registrationEssentials?.legalName} />
            <Field label="Tax Classification" value={nfp.registrationEssentials?.taxClassification} />
            <Field label="Organization Type" value={organizationTypeLabel(nfp.registrationEssentials?.orgStructure)} />
            <Field label="Status Recognition" value={recognitionLabel(nfp.registrationEssentials?.statusRecognition)} />
            <Field label="EIN" value={nfp.registrationEssentials?.ein} />
            <Field label="Parent Organization Name" value={nfp.registrationEssentials?.parentOrganizationName} />
            <Field label="Parent EIN" value={parentEinDisplay} />
            <Field label="Group Exemption Number (GEN)" value={nfp.registrationEssentials?.groupExemptionNumber} />
            <Field label="Recognized by Parent" value={nfp.registrationEssentials?.isRecognizedByParent} />
            <Field label="Covered by Parent Group Exemption" value={nfp.registrationEssentials?.isCoveredByParentGroupExemption} />
            <Field label="Chapter / Unit ID" value={nfp.registrationEssentials?.chapterUnitId} />
            <Field label="Government Entity Name" value={nfp.registrationEssentials?.governmentEntityName} />
            <Field label="Government Division" value={nfp.registrationEssentials?.governmentDivision} />
            <Field label="Recognition Notes" value={nfp.registrationEssentials?.recognitionNotes} />
            <Field label="Signatory Acceptance" value={nfp.registrationEssentials?.signatoryAcceptedAt ? new Date(nfp.registrationEssentials.signatoryAcceptedAt).toLocaleString() : 'not accepted'} />
            <Field label="Areas Served" value={(nfp.registrationEssentials?.areasServed || []).join(', ') || '—'} />
            <Field label="Mission Categories" value={(nfp.registrationEssentials?.missionCategories || []).join(', ') || '—'} />
            <Field label="Classification" value={nfp.registrationEssentials?.classification} />
            <Field label="Payout Intent Mode" value={nfp.registrationEssentials?.payoutIntentMode} />
          </article>

          <article
            className="ops-nfp-panel"
            style={{
              marginTop: 14,
              ...(geographyFocused ? { borderColor: '#f59e0b', boxShadow: '0 0 0 2px rgba(245, 158, 11, 0.18)' } : {}),
            }}
          >
            <h3 className="ops-section-title">Geography Resolution</h3>
            {geographyFocused || geographyReview.needsReview ? (
              <div className="ops-message" style={{ marginTop: 0, marginBottom: 12 }}>
                {geographyReview.guidance}
              </div>
            ) : null}
            <Field
              label="Root Market Key"
              value={geographyReview.rootMarketKey || '—'}
              help="The canonical market assigned to the NFP root/HQ. A market key can exist even when the root geography still needs manual verification."
            />
            <Field
              label="Root Verification"
              value={geographyReview.statusLabel}
              help="Resolved means root/HQ geography has been verified. Not verified means Ops should confirm, retry, or mark out-of-footprint."
            />
            <Field
              label="Review Reason"
              value={geographyReview.reasonLabel}
              help="Why this NFP is in geography review."
            />
            <Field
              label="HQ Market Key"
              value={geographyReview.hqMarketKey || '—'}
              help="The market inferred directly from the NFP headquarters payload."
            />
            <Field
              label="Service Areas"
              value={(nfp.geography?.serviceAreaKeys || []).join(', ') || '—'}
              help={geographyReview.serviceAreaHelp}
            />
            {nfp.adminActions?.canManageMarketOperations ? (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                <button
                  type="button"
                  className="ops-btn"
                  onClick={() => runGeographyAction('retry')}
                  title="Use when the address/place data changed or Google may now resolve the root/HQ geography."
                >
                  Retry Google Resolution
                </button>
                <button
                  type="button"
                  className="ops-btn"
                  onClick={() => runGeographyAction('resolve')}
                  title="Use when Ops has confirmed the displayed root/HQ market key or wants to choose another canonical market."
                >
                  Confirm Root Market
                </button>
                <button
                  type="button"
                  className="ops-btn danger"
                  onClick={() => runGeographyAction('out_of_footprint')}
                  title="Use only when the NFP root/HQ is intentionally outside the current operating footprint."
                >
                  Mark Out of Footprint
                </button>
              </div>
            ) : null}
          </article>

          <div className="ops-nfp-stack">
            <article className="ops-nfp-panel" style={{ marginTop: 0 }}>
              <h3 className="ops-section-title">Payout Readiness</h3>
              <Field label="payoutReadinessStatus" value={nfp.payoutPanel?.payoutReadinessStatus} />
              <Field label="payoutBlockedReason" value={nfp.payoutPanel?.payoutBlockedReason} />
              <Field
                label="First Eligible Payout Date"
                value={
                  nfp.payoutPanel?.firstEligiblePayoutDate
                    ? new Date(nfp.payoutPanel.firstEligiblePayoutDate).toLocaleString()
                    : '—'
                }
              />
              <Field label="Stripe connected account present" value={String(!!nfp.payoutPanel?.stripeConnected)} />
              <Field label="Connected account ID" value={nfp.payoutPanel?.stripeConnectedAccountId} />
              <Field label="Stripe transfers capability" value={nfp.payoutPanel?.stripeTransfersCapability} />
              <Field label="Stripe payouts capability" value={nfp.payoutPanel?.stripePayoutsCapability} />
              {nfp.payoutPanel?.warningVisibleNotPayable && (
                <div className="ops-message error" style={{ marginTop: 12 }}>
                  Visible in app but cannot be paid.
                </div>
              )}
            </article>
            <article className="ops-nfp-panel" style={{ marginTop: 0 }}>
              <h3 className="ops-section-title">Ops Context</h3>
              <Field label="Registered by (name)" value={nfp.payoutPanel?.opsContext?.registrantName} />
              <Field label="Registrant role" value={nfp.payoutPanel?.opsContext?.registrantRole} />
              <Field label="Registrant email" value={nfp.payoutPanel?.opsContext?.registrantEmail} />
              <Field label="Registrant phone" value={nfp.payoutPanel?.opsContext?.registrantPhone} />
              <Field label="Registered by user ID" value={nfp.payoutPanel?.opsContext?.registeredByUserId} />
              <Field label="HQ / Primary address" value={nfp.payoutPanel?.opsContext?.hqAddress} />
              <Field label="# locations registered" value={nfp.payoutPanel?.opsContext?.locationCount} />
              <Field label="Signatory name" value={nfp.payoutPanel?.opsContext?.signatoryName} />
              <Field label="Signatory email" value={nfp.payoutPanel?.opsContext?.signatoryEmail} />
              <Field
                label="Signatory different from registrant"
                value={nfp.payoutPanel?.opsContext?.signatoryDifferentFromRegistrant ? 'Yes' : 'No'}
              />
            </article>
          </div>
        </div>
      </section>

      <section className="ops-nfp-panel">
        <h3 className="ops-section-title">Audit Timeline</h3>
        {(nfp.auditTimeline || []).length === 0 && <div>No audit entries.</div>}
        {(nfp.auditTimeline || []).map((entry, idx) => (
          <div className="ops-kv" key={`${entry.field}-${idx}`}>
            <div className="k">{entry.field}</div>
            <div className="v">
              <div>
                {formatAuditValue(entry.from)} {' -> '} {formatAuditValue(entry.to)}
              </div>
              <div>actor: {entry.actor || '—'}</div>
              <div>at: {entry.at ? new Date(entry.at).toLocaleString() : '—'}</div>
              <div>reasonCode: {entry.reasonCode || '—'}</div>
              <div>reasonNote: {entry.reasonNote || '—'}</div>
              <div>override: {String(!!entry.overrideTransition)}</div>
            </div>
          </div>
        ))}
      </section>
      {previewAsset && (
        <div className="ops-preview-backdrop" onClick={() => setPreviewAsset(null)}>
          <div className="ops-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ops-preview-header">
              <strong>{previewAsset.label}</strong>
              <button type="button" className="ops-btn" onClick={() => setPreviewAsset(null)}>Close</button>
            </div>
            <div className="ops-preview-body">
              {isImageUrl(previewAsset.url) ? (
                <img src={previewAsset.url} alt={previewAsset.label} />
              ) : isPdfUrl(previewAsset.url) ? (
                <object
                  data={previewAsset.url}
                  type="application/pdf"
                  style={{ width: '100%', height: '70vh', border: 0, background: '#fff' }}
                >
                  <iframe
                    src={previewAsset.url}
                    title={previewAsset.label}
                    style={{ width: '100%', height: '70vh', border: 0, background: '#fff' }}
                  />
                </object>
              ) : (
                <div className="ops-preview-unavailable">
                  Inline preview is unavailable for this document type. Use Download.
                </div>
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              <a href={previewAsset.url} download>Download</a>
            </div>
          </div>
        </div>
      )}
      {proofUploadOpen && (
        <div className="ops-preview-backdrop" onClick={() => setProofUploadOpen(false)}>
          <div className="ops-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ops-preview-header">
              <strong>Upload Proof Document</strong>
              <button type="button" className="ops-btn" onClick={() => setProofUploadOpen(false)}>Close</button>
            </div>
            <div className="ops-nfp-field">
              <label>Document Type</label>
              <select
                title="Choose a proof document type."
                value={proofUploadType}
                onChange={(e) => setProofUploadType(e.target.value)}
                disabled={uploadBusy}
              >
                <option value="letterOfDetermination">Letter of Determination</option>
                <option value="operatingUnderGenDoc">Parent Exemption / Group Ruling Document</option>
                <option value="governmentUnitDoc">Government Unit Document</option>
                <option value="chapterAuthorization">Parent Recognition / Coverage Proof</option>
                <option value="otherSupportingDoc">Other Supporting Documentation</option>
              </select>
            </div>
            <div className="ops-nfp-field" style={{ marginTop: 10 }}>
              <label>Choose File</label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,image/*"
                title="Choose a proof file."
                disabled={uploadBusy}
                onChange={onProofModalFileSelected}
              />
            </div>
          </div>
        </div>
      )}
      <Modal open={!!modalConfig && !!modalState} title={modalConfig?.label || 'Update'} onClose={closeActionModal}>
        {modalConfig && modalState ? (
          <div className="ops-action-form-fields">
            {(modalConfig.options || []).length > 0 ? (
              <div className="ops-nfp-field">
                <label>New Status</label>
                <select
                  title="Choose a new status."
                  value={modalState.status}
                  onChange={(e) => updateActionState(modalActionKey, { status: e.target.value, reasonCode: '', error: '', success: '' })}
                >
                  <option value="">Select status</option>
                  {modalConfig.options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            ) : null}
            {getReasonCodesForAction(modalConfig, modalState.status).length > 0 ? (
              <div className="ops-nfp-field">
                <label>Reason Code</label>
                <select
                  title="Choose a reason code."
                  value={modalState.reasonCode}
                  onChange={(e) => updateActionState(modalActionKey, { reasonCode: e.target.value })}
                >
                  <option value="">Select reason code</option>
                  {getReasonCodesForAction(modalConfig, modalState.status).map((code) => (
                    <option key={code} value={code}>{code}</option>
                  ))}
                </select>
              </div>
            ) : null}
            <div className="ops-nfp-field">
              <label>{modalActionKey === 'addNote' ? 'Note' : 'Audit Note'}</label>
              <textarea
                rows={4}
                title="Add an audit note."
                value={modalState.reasonNote}
                onChange={(e) => updateActionState(modalActionKey, { reasonNote: e.target.value })}
              />
            </div>
            {nfp.adminActions?.canOverrideTransition && modalActionKey !== 'addNote' ? (
              <label
                style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                title="Use only to override a blocked transition."
              >
                <input
                  type="checkbox"
                  checked={!!modalState.overrideTransition}
                  onChange={(e) => updateActionState(modalActionKey, { overrideTransition: e.target.checked })}
                />
                overrideTransition
              </label>
            ) : null}
            {modalState.blockedByTransition ? (
              <div className="ops-message error">
                This transition is blocked by the current state. Use override only if you have authority and provide audit reasons.
              </div>
            ) : null}
            {modalState.error ? <div className="ops-message error">{modalState.error}</div> : null}
            {modalState.success ? <div className="ops-message">{modalState.success}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="ops-btn" onClick={closeActionModal}>Cancel</button>
              <button
                type="button"
                className="ops-btn primary"
                disabled={modalState.loading}
                onClick={() => runAction(modalActionKey)}
              >
                {modalState.loading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
      <Modal open={imageModal.open} title="Mark Image Do Not Use" onClose={closeDoNotUseModal}>
        <div className="ops-action-form-fields">
          <div className="ops-card-caption" style={{ marginBottom: 8 }}>
            This marks the selected image as unavailable for hero or general consumer use.
          </div>
          <div className="ops-nfp-field">
            <label>Image</label>
            <div>{imageModal.imageLabel || 'Selected image'}</div>
          </div>
          <div className="ops-nfp-field">
            <label>Reason Code</label>
            <select
              value={imageModal.reasonCode}
              onChange={(e) => setImageModal((prev) => ({ ...prev, reasonCode: e.target.value, error: '' }))}
            >
              <option value="">Select reason code</option>
              {IMAGE_DO_NOT_USE_REASON_CODES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <div className="ops-nfp-field">
            <label>Reason Note</label>
            <textarea
              rows={4}
              value={imageModal.reasonNote}
              onChange={(e) => setImageModal((prev) => ({ ...prev, reasonNote: e.target.value, error: '' }))}
            />
          </div>
          {imageModal.error ? <div className="ops-message error">{imageModal.error}</div> : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="ops-btn" onClick={closeDoNotUseModal}>Cancel</button>
            <button
              type="button"
              className="ops-btn primary"
              disabled={imageModal.loading}
              onClick={saveDoNotUseFlag}
            >
              {imageModal.loading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={impactModalOpen} title="Impact Levels" onClose={closeImpactModal}>
        <div style={{ display: 'grid', gap: 18 }}>
          <div className="ops-card-caption" style={{ marginTop: 0 }}>
            Review the Community and Individual impact levels entered by the NFP. You can edit, add, or remove levels here.
          </div>

          {['community', 'individual'].map((segment) => {
            const rows = impactDraftRows.filter((row) => row.segment === segment);
            const label = segment === 'community' ? 'Community' : 'Individual';
            return (
              <div
                key={segment}
                style={{
                  border: '1px solid #dcecea',
                  borderRadius: 14,
                  padding: 14,
                  display: 'grid',
                  gap: 12,
                  background: '#f9fcfb',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <h4 style={{ margin: 0, color: '#0f4954' }}>{label}</h4>
                  <button
                    type="button"
                    className="ops-btn"
                    onClick={() => addImpactRow(segment)}
                    disabled={impactSaving}
                  >
                    Add {label} Level
                  </button>
                </div>

                {rows.length === 0 ? (
                  <div className="ops-card-caption" style={{ marginTop: 0 }}>No {label.toLowerCase()} impact levels added.</div>
                ) : rows.map((row, idx) => (
                  <div
                    key={row.id}
                    style={{
                      border: '1px solid rgba(15, 23, 42, 0.08)',
                      borderRadius: 12,
                      padding: 12,
                      display: 'grid',
                      gap: 10,
                      background: '#ffffff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <div style={{ fontWeight: 700, color: '#46666d' }}>{label} Level {idx + 1}</div>
                      <button
                        type="button"
                        className="ops-btn"
                        onClick={() => removeImpactRow(row.id)}
                        disabled={impactSaving}
                        title="Remove this impact level."
                      >
                        Remove
                      </button>
                    </div>

                    <div className="ops-nfp-field" style={{ marginBottom: 0 }}>
                      <label>Threshold Amount ($)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.thresholdDollars}
                        onChange={(e) => updateImpactRow(row.id, { thresholdDollars: e.target.value })}
                        disabled={impactSaving}
                      />
                    </div>

                    <div className="ops-nfp-field" style={{ marginBottom: 0 }}>
                      <label>Description</label>
                      <textarea
                        rows={3}
                        maxLength={300}
                        value={row.description}
                        onChange={(e) => updateImpactRow(row.id, { description: e.target.value })}
                        disabled={impactSaving}
                        placeholder="Describe the impact at this level."
                      />
                    </div>

                    <div className="ops-nfp-field" style={{ marginBottom: 0 }}>
                      <label>Evidence Note (optional)</label>
                      <textarea
                        rows={2}
                        maxLength={300}
                        value={row.evidenceNote}
                        onChange={(e) => updateImpactRow(row.id, { evidenceNote: e.target.value })}
                        disabled={impactSaving}
                        placeholder="Optional context or proof note."
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })}

          {impactError ? <div className="ops-message error">{impactError}</div> : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="ops-btn" onClick={closeImpactModal} disabled={impactSaving}>Cancel</button>
            <button
              type="button"
              className="ops-btn primary"
              onClick={saveImpactMilestones}
              disabled={impactSaving}
            >
              {impactSaving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>
      <Modal open={profilePreviewOpen} title="Cause Detail Preview" onClose={closeProfilePreview}>
        <div className="ops-action-form-fields">
          {(() => {
            const activeImages = Array.isArray(nfp?.profileModeration?.images)
              ? nfp.profileModeration.images.filter((img) => !img?.isDeleted)
              : [];
            const liveImages = Array.isArray(nfp?.profileModeration?.liveImages)
              ? nfp.profileModeration.liveImages.filter((img) => !img?.isDeleted)
              : activeImages;
            const liveProfile = nfp?.profileModeration?.liveProfile || {};
            const proposedProfile = nfp?.profileModeration?.proposedProfile || {};
            const currentHeroUrl = nfp?.profileModeration?.heroImageUrl || '';
            const liveView = {
              shortDescription: liveProfile.shortDescription || nfp?.profileModeration?.shortDescription || '',
              whySupportShort: liveProfile.whySupportShort || nfp?.profileModeration?.whySupportShort || '',
              heroImageUrl: liveProfile.heroImageUrl || currentHeroUrl || '',
              heroMode: normalizeHeroMode(liveProfile.heroMode || nfp?.profileModeration?.heroMode),
            };
            const draftView = {
              shortDescription: profileDraft.shortDescription || '',
              whySupportShort: profileDraft.whySupportShort || '',
              heroImageUrl: profileDraft.heroImageUrl || '',
              heroMode: normalizeHeroMode(profileDraft.heroMode),
            };
            const proposedView = {
              shortDescription: proposedProfile.shortDescription || '',
              whySupportShort: proposedProfile.whySupportShort || '',
              heroImageUrl: proposedProfile.heroImageUrl || '',
              heroMode: normalizeHeroMode(proposedProfile.heroMode || nfp?.profileModeration?.heroMode),
            };
            const viewProfile =
              profilePreviewMode === 'live' ? liveView :
              profilePreviewMode === 'proposed' ? proposedView :
              draftView;
            const previewImages = profilePreviewMode === 'live' ? liveImages : activeImages;
            const viewHeroRef = cleanAssetUrl(viewProfile.heroImageUrl || '');
            const selectedHero = previewImages.find((img) => cleanAssetUrl(img?.url || '') === viewHeroRef) || null;
            const isDraftView = profilePreviewMode === 'draft';
            return (
              <>
                <div
                  style={{
                    display: 'grid',
                    gap: 18,
                    gridTemplateColumns: 'minmax(300px, 380px) minmax(0, 1fr)',
                    alignItems: 'start',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <div
                      style={{
                        width: 340,
                        height: 700,
                        borderRadius: 40,
                        padding: 14,
                        background: 'linear-gradient(180deg, #182c35 0%, #0f2028 100%)',
                        boxShadow: '0 24px 60px rgba(9, 23, 30, 0.28)',
                      }}
                      >
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: 30,
                            overflow: 'hidden',
                            background: 'linear-gradient(180deg, #124b4f 0%, #103c44 100%)',
                            color: '#f2f7f7',
                            border: '1px solid rgba(207, 230, 232, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <CauseConsumerPreview
                            name={nfp?.name || 'Cause Name'}
                            impactLabel={nfp?.registrationEssentials?.classification || 'Local Impact'}
                            categoryLabel={(nfp?.profileModeration?.missionTags || nfp?.registrationEssentials?.missionCategories || [])[0] || 'Cause'}
                            missionText={viewProfile.shortDescription || 'Short description preview will appear here.'}
                            whySupportText={viewProfile.whySupportShort || 'Why Support preview will appear here.'}
                            heroUrl={selectedHero?.url || viewProfile.heroImageUrl || ''}
                            heroMode={viewProfile.heroMode}
                            showSupportButton
                            style={{
                              flex: 1,
                              overflowY: 'auto',
                              WebkitOverflowScrolling: 'touch',
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  <div style={{ display: 'grid', gap: 14 }}>
                    {nfp?.profileModeration?.hasProposedChangesToReview ? (
                      <div className="ops-message warn" style={{ margin: 0 }}>
                        NFP dashboard changes are waiting for review. Compare the Proposed tab against Current Live before publishing.
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {[
                        ['live', 'Current Live'],
                        ['draft', 'Ops Draft'],
                        ['proposed', 'Proposed (NFP)'],
                      ].map(([mode, label]) => (
                        <button
                          key={mode}
                          type="button"
                          className="ops-btn"
                          onClick={() => setProfilePreviewMode(mode)}
                          style={{
                            background: profilePreviewMode === mode ? '#1f6f78' : '#ffffff',
                            color: profilePreviewMode === mode ? '#ffffff' : '#1a4d57',
                            borderColor: profilePreviewMode === mode ? '#1f6f78' : undefined,
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="ops-nfp-field">
                      <label>Hero Image</label>
                      {currentHeroUrl ? (
                        <div style={{ display: 'grid', gap: 8 }}>
                          <img
                            src={currentHeroUrl}
                            alt="Current live hero"
                            style={{
                              width: '100%',
                              maxHeight: 160,
                              objectFit: liveView.heroMode === 'graphic' ? 'contain' : 'cover',
                              borderRadius: 8,
                              display: 'block',
                              background: 'rgba(31,111,120,0.08)',
                            }}
                          />
                          <div className="ops-card-caption">Current live hero URL. Use the phone preview for the actual app presentation.</div>
                        </div>
                      ) : (
                        <div className="ops-card-caption">No live hero is currently set.</div>
                      )}
                    </div>
                    <div className="ops-nfp-field">
                      <label>
                        {isDraftView ? 'Choose Draft Hero' : profilePreviewMode === 'live' ? 'Live Hero Selection' : 'Proposed Hero Selection'}
                      </label>
                      {previewImages.length ? (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: 10 }}>
                          {previewImages.map((img, idx) => {
                            const imageUrl = String(img?.url || '');
                            const imageRef = cleanAssetUrl(imageUrl);
                            const isSelected = !!imageRef && imageRef === cleanAssetUrl(viewProfile.heroImageUrl || '');
                            const isCurrent = !!imageRef
                              && !!currentHeroUrl
                              && imageRef === cleanAssetUrl(currentHeroUrl);
                            return (
                              <button
                                key={`${imageUrl || idx}`}
                                type="button"
                                onClick={() => {
                                  if (!isDraftView) return;
                                  setProfileDraft((prev) => ({
                                    ...prev,
                                    heroImageUrl: imageRef,
                                    heroMode: normalizeHeroMode(img?.heroMode || prev.heroMode),
                                    error: '',
                                  }));
                                }}
                                disabled={!isDraftView}
                                style={{
                                  border: isSelected ? '2px solid #1f6f78' : '1px solid rgba(31,111,120,0.18)',
                                  borderRadius: 12,
                                  padding: 6,
                                  background: isSelected ? 'rgba(31,111,120,0.08)' : '#fff',
                                  cursor: isDraftView ? 'pointer' : 'default',
                                  textAlign: 'left',
                                  opacity: isDraftView ? 1 : 0.95,
                                }}
                                title={isDraftView ? 'Select this image for the Ops draft preview.' : 'Read-only in this view.'}
                              >
                                <img
                                  src={img.url}
                                  alt={img.alt || `Hero option ${idx + 1}`}
                                  style={{ width: '100%', height: 64, objectFit: 'cover', borderRadius: 8, display: 'block' }}
                                />
                                <div style={{ marginTop: 6, display: 'grid', gap: 4 }}>
                                  {isCurrent ? <span className="ops-chip good">Current Live Hero</span> : null}
                                  {isSelected && (!isDraftView || !isCurrent) ? (
                                    <span className={`ops-chip ${isCurrent ? 'good' : ''}`}>
                                      {isDraftView ? 'Draft Hero' : (profilePreviewMode === 'live' ? 'Live Selection' : 'Proposed Selection')}
                                    </span>
                                  ) : null}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="ops-card-caption">No active images available. Upload images before setting a hero.</div>
                      )}
                    </div>
                    <div className="ops-nfp-field">
                      <label>Hero Display Mode</label>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {['photo', 'graphic'].map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className="ops-btn"
                            disabled={!isDraftView}
                            onClick={() => setProfileDraft((prev) => ({ ...prev, heroMode: mode, error: '' }))}
                            style={{
                              background: viewProfile.heroMode === mode ? '#1f6f78' : '#ffffff',
                              color: viewProfile.heroMode === mode ? '#ffffff' : '#1a4d57',
                              borderColor: viewProfile.heroMode === mode ? '#1f6f78' : undefined,
                              cursor: isDraftView ? 'pointer' : 'default',
                            }}
                          >
                            {mode === 'photo' ? 'Photo: crop allowed' : 'Graphic: show full image'}
                          </button>
                        ))}
                      </div>
                      <div className="ops-card-caption">
                        Photos can crop. Graphics, posters, logos, and text-heavy creative should use Graphic.
                      </div>
                    </div>
                    <div className="ops-nfp-field">
                      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span>Short Description</span>
                        {isDraftView ? (
                          <button
                            type="button"
                            className="ops-btn"
                            onClick={() => setProfileDraft((prev) => ({ ...prev, editingShortDescription: !prev.editingShortDescription }))}
                            title="Toggle edit mode for short description."
                          >
                            {profileDraft.editingShortDescription ? 'Done' : '✎ Edit'}
                          </button>
                        ) : (
                          <span className="ops-card-caption">{profilePreviewMode === 'live' ? 'Live version' : 'NFP proposed version'}</span>
                        )}
                      </label>
                      {isDraftView && profileDraft.editingShortDescription ? (
                        <textarea
                          rows={3}
                          value={profileDraft.shortDescription}
                          onChange={(e) => setProfileDraft((prev) => ({ ...prev, shortDescription: e.target.value, error: '' }))}
                        />
                      ) : (
                        <div style={{ minHeight: 54, padding: '12px 14px', border: '1px solid rgba(31,111,120,0.14)', borderRadius: 12, background: '#f7fbfa' }}>
                          {viewProfile.shortDescription || 'No short description set.'}
                        </div>
                      )}
                      <div className="ops-card-caption">{charCountLabel(viewProfile.shortDescription, 180)}</div>
                    </div>
                    <div className="ops-nfp-field">
                      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span>Why Support</span>
                        {isDraftView ? (
                          <button
                            type="button"
                            className="ops-btn"
                            onClick={() => setProfileDraft((prev) => ({ ...prev, editingWhySupportShort: !prev.editingWhySupportShort }))}
                            title="Toggle edit mode for Why Support."
                          >
                            {profileDraft.editingWhySupportShort ? 'Done' : '✎ Edit'}
                          </button>
                        ) : (
                          <span className="ops-card-caption">{profilePreviewMode === 'live' ? 'Live version' : 'NFP proposed version'}</span>
                        )}
                      </label>
                      {isDraftView && profileDraft.editingWhySupportShort ? (
                        <textarea
                          rows={6}
                          value={profileDraft.whySupportShort}
                          onChange={(e) => setProfileDraft((prev) => ({ ...prev, whySupportShort: e.target.value, error: '' }))}
                        />
                      ) : (
                        <div style={{ minHeight: 110, padding: '12px 14px', border: '1px solid rgba(31,111,120,0.14)', borderRadius: 12, background: '#f7fbfa', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                          {viewProfile.whySupportShort || 'No Why Support text set.'}
                        </div>
                      )}
                      <div className="ops-card-caption">{charCountLabel(viewProfile.whySupportShort, 800)}</div>
                    </div>
                    {isDraftView && profileDraft.error ? <div className="ops-message error">{profileDraft.error}</div> : null}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                      <button type="button" className="ops-btn" onClick={closeProfilePreview}>Cancel</button>
                      {isDraftView ? (
                        <>
                          <button
                            type="button"
                            className="ops-btn"
                            disabled={profileDraft.loading}
                            onClick={() => saveProfilePreview(false)}
                          >
                            {profileDraft.loading ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            className="ops-btn primary"
                            disabled={profileDraft.loading}
                            onClick={() => saveProfilePreview(true)}
                          >
                            {profileDraft.loading ? 'Saving…' : 'Set as Live Profile'}
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </Modal>
      <Modal open={imageEditor.open} title="Image Review" onClose={closeImageEditor}>
        <div className="ops-action-form-fields">
          <div className="ops-nfp-field">
            <label>Reviewing Image</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <span>{imageEditor.imageLabel || 'Selected image'}</span>
              {imageEditor.isHero ? <span className="ops-chip good">Current Live Hero</span> : null}
              {imageEditor.doNotUse ? <span className="ops-chip bad">Do Not Use</span> : null}
              {imageEditor.isDeleted ? <span className="ops-chip">Soft Deleted</span> : null}
            </div>
          </div>
          {imageEditor.imageUrl ? (
            <div
              style={{
                borderRadius: 14,
                overflow: 'hidden',
                background: '#f7fbfa',
                border: '1px solid rgba(31, 111, 120, 0.14)',
              }}
            >
              <img
                src={imageEditor.imageUrl}
                alt={imageEditor.imageLabel}
                style={{ width: '100%', maxHeight: '48vh', objectFit: 'contain', display: 'block', background: '#f7fbfa' }}
              />
            </div>
          ) : null}
          <div className="ops-nfp-field">
            <label>Tags</label>
            <input
              type="text"
              value={imageEditor.tagsText}
              onChange={(e) => setImageEditor((prev) => ({ ...prev, tagsText: e.target.value, error: '' }))}
              placeholder="community, event, volunteers"
            />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" className="ops-btn" disabled={imageEditor.loading} onClick={saveImageTags}>
              {imageEditor.loading ? 'Saving…' : 'Save Tags'}
            </button>
            <button
              type="button"
              className="ops-btn"
              disabled={imageEditor.loading || imageEditor.doNotUse || imageEditor.isDeleted || imageEditor.isHero}
              onClick={async () => {
                setImageEditor((prev) => ({ ...prev, loading: true, error: '' }));
                try {
                  await saveProfileAssets({ heroImageUrl: cleanAssetUrl(imageEditor.imageUrl) });
                  setToastMessage('Updated');
                  setImageEditor((prev) => ({ ...prev, loading: false, isHero: true }));
                } catch (err) {
                  setImageEditor((prev) => ({
                    ...prev,
                    loading: false,
                    error: err?.data?.error || err?.message || 'Failed to update hero image',
                  }));
                }
              }}
            >
              {imageEditor.isHero ? 'Current Hero' : 'Set as Hero'}
            </button>
            {imageEditor.doNotUse ? (
              <button
                type="button"
                className="ops-btn"
                disabled={imageEditor.loading}
                onClick={async () => {
                  setImageEditor((prev) => ({ ...prev, loading: true, error: '' }));
                  try {
                    await saveProfileAssets({ imageGovernance: [{ url: imageEditor.imageUrl, doNotUse: false }] });
                    setToastMessage('Updated');
                    setImageEditor((prev) => ({ ...prev, loading: false, doNotUse: false }));
                  } catch (err) {
                    setImageEditor((prev) => ({
                      ...prev,
                      loading: false,
                      error: err?.data?.error || err?.message || 'Failed to clear image restriction',
                    }));
                  }
                }}
              >
                Allow Use
              </button>
            ) : (
              <button
                type="button"
                className="ops-btn"
                disabled={imageEditor.loading}
                onClick={() => openDoNotUseModal({ url: imageEditor.imageUrl, alt: imageEditor.imageLabel })}
              >
                Mark Do Not Use
              </button>
            )}
            <button
              type="button"
              className="ops-btn"
              disabled={imageEditor.loading}
              onClick={() => toggleImageDeleted(!imageEditor.isDeleted)}
            >
              {imageEditor.isDeleted ? 'Restore Image' : 'Soft Delete'}
            </button>
          </div>
          {imageEditor.error ? <div className="ops-message error">{imageEditor.error}</div> : null}
        </div>
      </Modal>
      </div>
    </OpsLayout>
  );
}
